import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24">
        <SEO title="Shopping Cart - Gangadhar Som" noindex />
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20">
            <ShoppingBag size={56} className="mx-auto mb-5 text-muted-foreground" />
            <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light mb-3">Your Cart is Empty</h1>
            <p className="font-sans text-sm sm:text-base text-muted-foreground mb-6">Add some beautiful artwork to your cart!</p>
            <Link to="/gallery">
              <Button className="bg-accent hover:bg-accent/90">
                Browse Gallery <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24">
      <SEO title="Shopping Cart - Gangadhar Som" noindex />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mb-8 sm:mb-12">
          Shopping Cart ({getCartCount()} items)
        </h1>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {cartItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-lg p-4 sm:p-6 border border-border"
              >
                <div className="flex gap-3 sm:gap-6">
                  <div className="artwork-frame w-20 h-20 sm:w-32 sm:h-32 flex-shrink-0 flex items-center justify-center">
                    <img src={item.image_url} alt={item.title} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-base sm:text-xl font-light mb-1 truncate">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                      {item.art_type} {item.category ? `· ${item.category}` : ''}
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-border flex items-center justify-center hover:bg-accent hover:text-white transition-colors">
                          <Minus size={14} />
                        </button>
                        <span className="font-sans font-medium w-6 text-center text-sm">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-border flex items-center justify-center hover:bg-accent hover:text-white transition-colors">
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                        <p className="font-serif text-lg sm:text-2xl font-light">{formatPrice(item.price * item.quantity)}</p>
                        <button onClick={() => removeFromCart(item.id)}
                          className="text-xs text-destructive hover:underline flex items-center gap-1">
                          <Trash2 size={12} /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg p-5 sm:p-6 border border-border lg:sticky lg:top-24">
              <h2 className="font-serif text-xl sm:text-2xl font-light mb-5">Order Summary</h2>
              <div className="space-y-3 mb-5">
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatPrice(getCartTotal())}</span>
                </div>
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-xs">Calculated at checkout</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-serif text-lg">Total</span>
                  <span className="font-serif text-xl sm:text-2xl font-medium">{formatPrice(getCartTotal())}</span>
                </div>
              </div>
              <Button onClick={() => navigate('/checkout')} className="w-full bg-accent hover:bg-accent/90 mb-3">
                Proceed to Checkout <ArrowRight size={16} className="ml-2" />
              </Button>
              <Link to="/gallery">
                <Button variant="outline" className="w-full">Continue Shopping</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
